import GrailedAPIError from "storefront/GrailedAPI/Error";
import GrailedAPIFetchError from "storefront/GrailedAPI/FetchError";

// The query string is dropped and numeric ids are collapsed so one endpoint does not fan out into
// an issue per distinct set of params or per record. The base only matters for relative urls, whose
// pathname is all we keep anyway.
const pathOf = (url: string): string | undefined => {
  try {
    return new URL(url, "http://localhost").pathname
      .split("/")
      .map((segment) => (/^\d+$/.test(segment) ? ":id" : segment))
      .join("/");
  } catch {
    return undefined;
  }
};

/**
 * Every GrailedAPIError is constructed in one shared helper, so all of them reach Sentry with an
 * identical two-frame stack. Sentry groups by stack, so unrelated endpoints and statuses collapse
 * into a single issue that can never be meaningfully resolved. Grouping by status and path splits
 * them apart.
 */
export const fingerprintGrailedAPIError = (
  error: GrailedAPIError,
): Array<string> | undefined => {
  const { status, url } = error.response;
  if (!status || !url) return undefined;

  const path = pathOf(url);
  return path ? ["GrailedAPIError", String(status), path] : undefined;
};

/**
 * GrailedAPIFetchError has the same problem: one helper builds every instance, so a timeout on one
 * endpoint and a dropped connection on another would share an issue. Grouping by kind and path
 * splits them apart.
 */
export const fingerprintGrailedAPIFetchError = (
  error: GrailedAPIFetchError,
): Array<string> | undefined => {
  if (!error.url) return undefined;

  const path = pathOf(error.url);
  return path ? ["GrailedAPIFetchError", error.kind, path] : undefined;
};
